/**
 * ByteTrack Multi-Object Tracker
 *
 * Adapted from: https://github.com/nomi30701/YOLO-ByteTrack-ONNX-Web
 *
 * Provides consistent object identity across frames with:
 * - Kalman filter for position prediction
 * - Hungarian algorithm for optimal assignment
 * - Two-stage association (high confidence first, then low confidence)
 *
 * Input format: { x, y, width, height, confidence } (center coordinates)
 * Output format: { x, y, width, height, confidence, trackId, interpolated? }
 */

import * as math from 'mathjs';
import { minWeightAssign } from 'munkres-algorithm';

// Configuration - simplified for direct video collection (no long track buffer)
const BYTE_TRACKER_CONFIG = {
  trackHighThresh: 0.25,   // Threshold for first association
  trackLowThresh: 0.1,     // Threshold for second association
  newTrackThresh: 0.25,    // Threshold for initializing new tracks
  trackBuffer: 3,          // Frames to keep lost tracks (small - collecting video directly)
  matchThresh: 0.8,        // IoU threshold for matching
};

/**
 * Single Track class - manages individual object tracking state
 */
class STrack {
  constructor(trackId, xywh, score, kalmanFilter) {
    this.trackId = trackId;
    this.xywh = xywh; // [x_center, y_center, width, height]
    this.score = score;
    this.kalmanFilter = kalmanFilter;
    this.state = 'Tracked'; // Tracked, Lost, Removed
    [this.mean, this.covariance] = kalmanFilter.initiate(xywh);
    this.isActivated = false;
    this.frameId = 0;
  }

  /**
   * Convert center coordinates to top-left + width/height
   */
  xywhToTlwh(xywh) {
    return [xywh[0] - xywh[2] / 2, xywh[1] - xywh[3] / 2, xywh[2], xywh[3]];
  }

  /**
   * Predict next state using Kalman filter
   */
  predict() {
    [this.mean, this.covariance] = this.kalmanFilter.predict(
      this.mean,
      this.covariance
    );
    // Update xywh from predicted mean
    // Clamp width/height to minimum positive value to avoid invalid boxes
    // (Kalman filter can produce negative values during prediction)
    const MIN_SIZE = 1;
    this.xywh = [
      this.mean.get([0]),
      this.mean.get([1]),
      Math.max(this.mean.get([2]), MIN_SIZE),
      Math.max(this.mean.get([3]), MIN_SIZE),
    ];
  }

  /**
   * Update track with new detection
   */
  update(newTrack, frameId) {
    this.xywh = newTrack.xywh.slice();
    this.score = newTrack.score;
    [this.mean, this.covariance] = this.kalmanFilter.update(
      this.mean,
      this.covariance,
      this.xywh
    );
    this.frameId = frameId;
    this.state = 'Tracked';
    this.isActivated = true;
  }

  markLost() {
    this.state = 'Lost';
  }

  markRemoved() {
    this.state = 'Removed';
  }

  /**
   * Get current position in our format
   * Ensures width/height are always positive (minimum 1)
   */
  toDetection() {
    const MIN_SIZE = 1;
    return {
      x: this.xywh[0],
      y: this.xywh[1],
      width: Math.max(this.xywh[2], MIN_SIZE),
      height: Math.max(this.xywh[3], MIN_SIZE),
      confidence: this.score,
      trackId: this.trackId,
    };
  }
}

/**
 * Object pool for STrack to reduce memory allocation
 */
class STrackPool {
  constructor(maxPoolSize = 100) {
    this.pool = [];
    this.maxPoolSize = maxPoolSize;
  }

  acquire(trackId, xywh, score, kalmanFilter) {
    if (this.pool.length > 0) {
      const track = this.pool.pop();
      track.trackId = trackId;
      track.xywh = xywh.slice();
      track.score = score;
      track.kalmanFilter = kalmanFilter;
      track.state = 'Tracked';
      track.isActivated = false;
      track.frameId = 0;
      [track.mean, track.covariance] = kalmanFilter.initiate(xywh);
      return track;
    }
    return new STrack(trackId, xywh, score, kalmanFilter);
  }

  release(track) {
    if (this.pool.length < this.maxPoolSize) {
      this.pool.push(track);
    }
  }
}

/**
 * Kalman Filter for position/velocity tracking
 * State: [x, y, w, h, vx, vy, vw, vh]
 */
class KalmanFilterXYWH {
  constructor() {
    // State transition matrix (constant velocity model)
    this.F = math.matrix([
      [1, 0, 0, 0, 1, 0, 0, 0],
      [0, 1, 0, 0, 0, 1, 0, 0],
      [0, 0, 1, 0, 0, 0, 1, 0],
      [0, 0, 0, 1, 0, 0, 0, 1],
      [0, 0, 0, 0, 1, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 0, 0],
      [0, 0, 0, 0, 0, 0, 1, 0],
      [0, 0, 0, 0, 0, 0, 0, 1],
    ]);
    // Observation matrix (we only observe position, not velocity)
    this.H = math.matrix([
      [1, 0, 0, 0, 0, 0, 0, 0],
      [0, 1, 0, 0, 0, 0, 0, 0],
      [0, 0, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 1, 0, 0, 0, 0],
    ]);
  }

  initiate(measurement) {
    const mean = math.matrix([...measurement, 0, 0, 0, 0]);
    const std = 2 * 0.05 * measurement[3]; // Based on height
    const P = math.diag([
      std * std,
      std * std,
      std * std,
      std * std,
      1e-2,
      1e-2,
      1e-2,
      1e-2,
    ]);
    return [mean, P];
  }

  predict(mean, covariance) {
    const stdPos = 0.05 * mean.get([3]);
    const stdVel = 0.5 * mean.get([3]);
    const Q = math.diag([
      stdPos * stdPos,
      stdPos * stdPos,
      stdPos * stdPos,
      stdPos * stdPos,
      stdVel * stdVel,
      stdVel * stdVel,
      stdVel * stdVel,
      stdVel * stdVel,
    ]);
    const motionMean = math.multiply(this.F, mean);
    const motionCov = math.add(
      math.multiply(math.multiply(this.F, covariance), math.transpose(this.F)),
      Q
    );
    return [motionMean, motionCov];
  }

  update(mean, covariance, measurement) {
    const stdPos = 0.05 * mean.get([3]);
    const stdW = 1e-1;
    const R = math.diag([
      stdPos * stdPos,
      stdPos * stdPos,
      stdW * stdW,
      stdPos * stdPos,
    ]);
    const z = math.matrix(measurement);
    const HCovar = math.multiply(this.H, covariance);
    const projectedMean = math.multiply(this.H, mean);
    const projectedCov = math.add(
      math.multiply(HCovar, math.transpose(this.H)),
      R
    );
    const K = math.multiply(
      covariance,
      math.transpose(this.H),
      math.inv(projectedCov)
    );
    const innovation = math.subtract(z, projectedMean);
    const updatedMean = math.add(mean, math.multiply(K, innovation));
    const updatedCov = math.subtract(covariance, math.multiply(K, HCovar));
    return [updatedMean, updatedCov];
  }
}

/**
 * Compute IoU between two boxes in tlwh format
 */
function computeIoU(box1, box2) {
  const [x1, y1, w1, h1] = box1;
  const [x2, y2, w2, h2] = box2;
  const xLeft = Math.max(x1, x2);
  const yTop = Math.max(y1, y2);
  const xRight = Math.min(x1 + w1, x2 + w2);
  const yBottom = Math.min(y1 + h1, y2 + h2);
  if (xRight <= xLeft || yBottom <= yTop) return 0;
  const intersection = (xRight - xLeft) * (yBottom - yTop);
  const union = w1 * h1 + w2 * h2 - intersection;
  return intersection / union;
}

/**
 * ByteTracker - Multi-object tracker using ByteTrack algorithm
 *
 * Usage:
 *   const tracker = new ByteTracker();
 *   const trackedDetections = tracker.update(detections);
 *
 * Input detections: [{ x, y, width, height, confidence }, ...]
 * Output: [{ x, y, width, height, confidence, trackId }, ...]
 */
export class ByteTracker {
  constructor(config = {}) {
    this.config = { ...BYTE_TRACKER_CONFIG, ...config };
    this.trackedSTracks = [];
    this.lostSTracks = [];
    this.frameId = 0;
    this.kalmanFilter = new KalmanFilterXYWH();
    this.trackIdCount = 0;
    this.trackPool = new STrackPool(200);
  }

  /**
   * Reset tracker state
   */
  reset() {
    this.trackedSTracks = [];
    this.lostSTracks = [];
    this.frameId = 0;
    this.trackIdCount = 0;
  }

  /**
   * Convert our detection format to internal format
   */
  detectionToXywh(det) {
    return [det.x, det.y, det.width, det.height];
  }

  /**
   * Update tracker with new detections
   * @param {Array} detections - [{x, y, width, height, confidence}, ...]
   * @returns {Array} - Tracked detections with trackId added
   */
  update(detections) {
    this.frameId++;

    // Convert detections to STrack format
    const dets = detections.map(det =>
      this.trackPool.acquire(
        -1,
        this.detectionToXywh(det),
        det.confidence,
        this.kalmanFilter
      )
    );

    // Predict next state for all existing tracks
    this.trackedSTracks.forEach(track => track.predict());
    this.lostSTracks.forEach(track => track.predict());

    // First association: high confidence detections with tracked objects
    const highScoreDets = dets.filter(
      det => det.score >= this.config.trackHighThresh
    );

    const [matched1, , unmatchedDets1] = this.match(
      this.trackedSTracks,
      highScoreDets
    );

    // Update matched tracks
    const keepTrackFlags = new Array(this.trackedSTracks.length).fill(false);
    matched1.forEach(([trackIdx, detIdx]) => {
      this.trackedSTracks[trackIdx].update(highScoreDets[detIdx], this.frameId);
      keepTrackFlags[trackIdx] = true;
    });

    // Mark unmatched tracks as lost
    for (let i = 0; i < this.trackedSTracks.length; i++) {
      if (!keepTrackFlags[i]) {
        this.trackedSTracks[i].markLost();
        this.lostSTracks.push(this.trackedSTracks[i]);
      }
    }

    // Keep only matched tracks
    this.trackedSTracks = this.trackedSTracks.filter((_, i) => keepTrackFlags[i]);

    // Second association: remaining detections with lost tracks
    const remainingDets = unmatchedDets1
      .map(idx => highScoreDets[idx])
      .concat(dets.filter(det => det.score < this.config.trackHighThresh));

    const [matched2] = this.match(this.lostSTracks, remainingDets);

    // Recover matched lost tracks
    const matchedLostIndices = new Set(matched2.map(([tIdx]) => tIdx));
    matched2.forEach(([trackIdx, detIdx]) => {
      this.lostSTracks[trackIdx].update(remainingDets[detIdx], this.frameId);
      this.trackedSTracks.push(this.lostSTracks[trackIdx]);
    });
    this.lostSTracks = this.lostSTracks.filter((_, idx) => !matchedLostIndices.has(idx));

    // Initialize new tracks from unmatched high-confidence detections
    const matchedDetIndices = new Set([
      ...matched1.map(([, dIdx]) => dIdx),
      ...matched2.map(([, dIdx]) => {
        // Map back to original det index
        const det = remainingDets[dIdx];
        return dets.indexOf(det);
      }),
    ]);

    dets.forEach((det, idx) => {
      if (!matchedDetIndices.has(idx) && det.score >= this.config.newTrackThresh) {
        det.trackId = this.trackIdCount++;
        det.frameId = this.frameId;
        this.trackedSTracks.push(det);
      }
    });

    // Remove tracks lost for too long
    this.lostSTracks = this.lostSTracks.filter(
      track => this.frameId - track.frameId <= this.config.trackBuffer
    );

    // Release unused tracks back to pool
    dets.forEach(det => {
      if (det.trackId === -1) {
        this.trackPool.release(det);
      }
    });

    // Return tracked detections in our format
    return this.trackedSTracks.map(track => track.toDetection());
  }

  /**
   * Get interpolated position for a lost track
   * @returns {Object|null} - Interpolated detection or null
   */
  getInterpolatedPosition(trackId) {
    const track = this.lostSTracks.find(t => t.trackId === trackId);
    if (!track) return null;

    return {
      ...track.toDetection(),
      confidence: 0,
      interpolated: true,
    };
  }

  /**
   * Get all current tracks including lost ones (with interpolated positions)
   */
  getAllTracks() {
    const tracked = this.trackedSTracks.map(t => ({
      ...t.toDetection(),
      interpolated: false,
    }));

    const lost = this.lostSTracks.map(t => ({
      ...t.toDetection(),
      confidence: 0,
      interpolated: true,
    }));

    return [...tracked, ...lost];
  }

  /**
   * Match tracks to detections using Hungarian algorithm
   */
  match(tracks, detections) {
    if (tracks.length === 0) {
      return [[], [], Array.from({ length: detections.length }, (_, i) => i)];
    }
    if (detections.length === 0) {
      return [[], Array.from({ length: tracks.length }, (_, i) => i), []];
    }

    const costMatrix = this.getDists(tracks, detections);
    const result = minWeightAssign(costMatrix);
    const assignments = result.assignments;

    const matched = [];
    const unmatchedTracks = [];
    const assignedDets = new Set();

    for (let tIdx = 0; tIdx < assignments.length; tIdx++) {
      const dIdx = assignments[tIdx];
      if (dIdx !== null && costMatrix[tIdx][dIdx] <= this.config.matchThresh) {
        matched.push([tIdx, dIdx]);
        assignedDets.add(dIdx);
      } else {
        unmatchedTracks.push(tIdx);
      }
    }

    const unmatchedDets = [];
    for (let dIdx = 0; dIdx < detections.length; dIdx++) {
      if (!assignedDets.has(dIdx)) {
        unmatchedDets.push(dIdx);
      }
    }

    return [matched, unmatchedTracks, unmatchedDets];
  }

  /**
   * Compute distance matrix (1 - IoU) between tracks and detections
   */
  getDists(tracks, detections) {
    const tracksTlwh = tracks.map(t => t.xywhToTlwh(t.xywh));
    const detsTlwh = detections.map(d => d.xywhToTlwh(d.xywh));

    return tracksTlwh.map((trackBox, i) => {
      const [tx, ty] = trackBox;
      const distThresh = (trackBox[2] + trackBox[3]) * 1.5;

      return detsTlwh.map((detBox) => {
        const [dx, dy] = detBox;
        // Quick distance check to skip far detections
        if (Math.abs(tx - dx) + Math.abs(ty - dy) > distThresh) {
          return 1;
        }
        return 1 - computeIoU(trackBox, detBox);
      });
    });
  }
}

export default ByteTracker;
